import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export interface ZernioProfile { id: string; name: string; description: string | null }
export interface ZernioProfilesResponse {
  profiles: ZernioProfile[];
  activeProfileId: string | null;
}

export function useZernioProfiles(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-profiles", brandId],
    queryFn: () => apiFetch<ZernioProfilesResponse>(`/brands/${brandId}/zernio/profiles`),
    enabled: !!brandId && enabled,
  });
}

export function useConnectZernio(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch(`/brands/${brandId}/zernio/connect`, {
        method: "POST",
        body: JSON.stringify({ apiKey }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand", brandId] });
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      qc.invalidateQueries({ queryKey: ["zernio-profiles", brandId] });
    },
  });
}

export function useDisconnectZernio(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/brands/${brandId}/zernio`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand", brandId] });
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
    },
  });
}

export function useCreateProfile(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      apiFetch<{ profile: ZernioProfile }>(`/brands/${brandId}/zernio/profile`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zernio-profiles", brandId] });
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      // Creating a profile also makes it active server-side, which clears the
      // ZERNIO_NOT_CONFIGURED gate on comment automations.
      qc.invalidateQueries({ queryKey: ["comment-automations", brandId] });
    },
  });
}

export function useSetActiveProfile(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) =>
      apiFetch(`/brands/${brandId}/zernio/profile`, {
        method: "PATCH",
        body: JSON.stringify({ profileId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zernio-profiles", brandId] });
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      // Comment automations 412 on a brand with no active profile and cache
      // that state for a minute — drop it so the page reflects the new profile.
      qc.invalidateQueries({ queryKey: ["comment-automations", brandId] });
    },
  });
}

export function useStartPlatformConnect(brandId: string) {
  return useMutation({
    mutationFn: (platform: string) =>
      apiFetch<{ authUrl: string; state: string }>(
        `/brands/${brandId}/zernio/connect-platform`,
        { method: "POST", body: JSON.stringify({ platform }) },
      ),
  });
}

export function useFinalizeConnect() {
  return useMutation({
    mutationFn: (state: string) =>
      apiFetch<{ brandSlug: string; platform: string }>(
        `/zernio/finalize-connect`,
        { method: "POST", body: JSON.stringify({ state }) },
      ),
  });
}

export function useZernioStatus() {
  return useQuery({
    queryKey: ["zernio-status"],
    queryFn: () => apiFetch<{ defaultKeySet: boolean }>("/zernio/status"),
    staleTime: 5 * 60_000,
  });
}
